// Stitches product grouping swatches onto Rebuy YMAL widget cards.
// Fetches ALL style: tag siblings from Storefront API (not just widget products) and
// transforms them into Rebuy-compatible product objects for full in-card swapping.
(function () {
  const STYLE_TAG_PREFIX = 'style:';
  const YMAL_WIDGET_ID = '294079';
  const groupCache = {};
  let storedWidget = null;

  function getStyleTag(product) {
    const tags = Array.isArray(product.tags)
      ? product.tags
      : (product.tags || '').split(',').map((t) => t.trim());
    return tags.find((t) => t.startsWith(STYLE_TAG_PREFIX)) || null;
  }

  function extractId(gid) {
    if (typeof gid === 'number') return gid;
    return parseInt(String(gid).split('/').pop(), 10);
  }

  async function storefrontFetch(query) {
    const { shop, version, token } = window.api || {};
    if (!token || !shop) return null;
    const res = await fetch(`https://${shop}/api/${version}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query }),
    });
    return res.json();
  }

  // Transform a Storefront API product node to the shape Rebuy's Vue template expects
  function storefrontToRebuy(node) {
    const productId = extractId(node.id);
    const mf = node.metafields || [];
    const swatchHex1 = mf.find((m) => m?.key === 'swatch_hexcode_1')?.value || null;
    const swatchHex2 = mf.find((m) => m?.key === 'swatch_hexcode_2')?.value || null;

    const variants = (node.variants?.nodes || []).map((v) => {
      const opts = v.selectedOptions || [];
      const price = Math.round(parseFloat(v.price?.amount || 0) * 100);
      const compareAtPrice = v.compareAtPrice
        ? Math.round(parseFloat(v.compareAtPrice.amount) * 100)
        : null;
      return {
        id: extractId(v.id),
        title: v.title,
        option1: opts[0]?.value || null,
        option2: opts[1]?.value || null,
        option3: opts[2]?.value || null,
        price,
        compare_at_price: compareAtPrice,
        available: v.availableForSale,
        featured_image: v.image ? { src: v.image.url, alt: v.image.altText || '' } : null,
        product_id: productId,
      };
    });

    const firstAvailable = variants.find((v) => v.available) || variants[0];
    const images = (node.images?.nodes || []).map((img) => ({
      src: img.url,
      alt: img.altText || '',
    }));
    const minPrice = variants.length ? Math.min(...variants.map((v) => v.price)) : 0;

    // Build options array from variant selectedOptions
    const optionMap = {};
    const optionOrder = [];
    (node.variants?.nodes || []).forEach((v) => {
      (v.selectedOptions || []).forEach((opt) => {
        if (!optionMap[opt.name]) {
          optionMap[opt.name] = new Set();
          optionOrder.push(opt.name);
        }
        optionMap[opt.name].add(opt.value);
      });
    });
    const options = optionOrder.map((name, i) => ({
      name,
      values: [...optionMap[name]],
      position: i + 1,
    }));

    const colorOption = options.find((o) => o.name.toLowerCase() === 'color');

    return {
      id: productId,
      handle: node.handle,
      title: node.title,
      url: '/products/' + node.handle,
      product_url: '/products/' + node.handle,
      price: minPrice,
      min_price: minPrice,
      compare_at_price: null,
      available: variants.some((v) => v.available),
      featured_image: images[0] || null,
      images,
      variants,
      options,
      selected_variant: firstAvailable || null,
      selected_variant_id: firstAvailable?.id || null,
      tags: Array.isArray(node.tags) ? node.tags.join(', ') : (node.tags || ''),
      status: 'ready',
      swatchHex1,
      swatchHex2,
      colorValue: colorOption
        ? (node.variants?.nodes?.[0]?.selectedOptions?.find(
            (o) => o.name.toLowerCase() === 'color'
          )?.value || '')
        : '',
    };
  }

  async function fetchProductGroup(styleTag) {
    if (groupCache[styleTag]) return groupCache[styleTag];

    const query = `
      {
        products(first: 25, query: "tag:\\"${styleTag}\\"") {
          nodes {
            id
            handle
            title
            tags
            images(first: 5) { nodes { url altText } }
            metafields(identifiers: [
              { namespace: "details", key: "swatch_hexcode_1" },
              { namespace: "details", key: "swatch_hexcode_2" }
            ]) { key value }
            variants(first: 50) {
              nodes {
                id
                title
                availableForSale
                price { amount }
                compareAtPrice { amount }
                image { url altText }
                selectedOptions { name value }
              }
            }
          }
        }
      }
    `;

    try {
      const json = await storefrontFetch(query);
      const nodes = json?.data?.products?.nodes || [];
      const group = nodes.map(storefrontToRebuy);
      groupCache[styleTag] = group;
      return group;
    } catch (err) {
      return [];
    }
  }

  async function fetchSingleProductHex(handle) {
    const query = `
      {
        product(handle: "${handle}") {
          metafields(identifiers: [
            { namespace: "details", key: "swatch_hexcode_1" },
            { namespace: "details", key: "swatch_hexcode_2" }
          ]) { key value }
        }
      }
    `;
    try {
      const json = await storefrontFetch(query);
      const mf = json?.data?.product?.metafields || [];
      return {
        swatchHex1: mf.find((m) => m?.key === 'swatch_hexcode_1')?.value || null,
        swatchHex2: mf.find((m) => m?.key === 'swatch_hexcode_2')?.value || null,
      };
    } catch (err) {
      return { swatchHex1: null, swatchHex2: null };
    }
  }

  async function stitchProduct(product) {
    const styleTag = getStyleTag(product);

    if (styleTag) {
      const grouping = await fetchProductGroup(styleTag);
      product.productGrouping = grouping;
      const self = grouping.find((g) => g.handle === product.handle);
      product.swatchHex1 = self?.swatchHex1 || null;
      product.swatchHex2 = self?.swatchHex2 || null;
      product.colorValue = self?.colorValue || '';
    } else {
      product.productGrouping = [];
      const hex = await fetchSingleProductHex(product.handle);
      product.swatchHex1 = hex.swatchHex1;
      product.swatchHex2 = hex.swatchHex2;
    }
  }

  // Adds _400x400 sizing to a Shopify CDN image URL
  function sizeShopifyImage(src, size) {
    if (!src) return src;
    return src.replace(/\.(jpe?g|png|gif|webp)(\?|$)/i, `_${size}.$1$2`);
  }

  // Intercept ALL grouped swatch clicks at capture phase — fires before Rebuy's handlers
  document.addEventListener('click', (e) => {
    const swatch = e.target.closest('.rebuy-grouping-swatch[data-group-handle]');
    if (!swatch) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    if (!storedWidget) return;

    const groupHandle = swatch.dataset.groupHandle;
    const productBlock = swatch.closest('.rebuy-product-block');
    if (!productBlock) return;

    const cardIndex = productBlock.dataset.cardIndex !== undefined
      ? parseInt(productBlock.dataset.cardIndex, 10)
      : null;
    if (cardIndex === null || isNaN(cardIndex)) return;

    const product = storedWidget.data.products[cardIndex];
    if (!product?.productGrouping) return;

    const group = product.productGrouping.find((g) => g.handle === groupHandle);
    if (!group || group.handle === product.handle) return;

    // Capture current non-color option values before swap — color slot varies by product type
    // (apparel: Color=option1, shoes: Size=option1 Color=option2) so identify by option name
    const colorOptionIdx = product.options
      ? product.options.findIndex((o) => o.name.toLowerCase() === 'color')
      : -1;
    const optionSlots = ['option1', 'option2', 'option3'];
    const preservedOptions = {};
    if (product.selected_variant) {
      optionSlots.forEach((key, idx) => {
        if (idx !== colorOptionIdx && product.selected_variant[key]) {
          preservedOptions[key] = product.selected_variant[key];
        }
      });
    }

    const savedGrouping = product.productGrouping;
    Object.assign(product, group, {
      productGrouping: savedGrouping,
      swatchHex1: group.swatchHex1,
      swatchHex2: group.swatchHex2,
    });

    // Ensure featured_image is the new product's first image
    product.featured_image = group.featured_image;
    product.images = group.images;

    // Try to match the same non-color options in the new product; fall back to first available
    let targetVariant = group.selected_variant;
    if (Object.keys(preservedOptions).length > 0) {
      const sizeMatch = product.variants.find((v) =>
        Object.entries(preservedOptions).every(([key, val]) => v[key] === val) &&
        v.available
      );
      if (sizeMatch) targetVariant = sizeMatch;
    }
    product.selected_variant = targetVariant;
    product.selected_variant_id = targetVariant?.id || null;

    // Directly update the DOM image — bypasses Vue rendering path to guarantee the image swaps
    const rawSrc = group.selected_variant?.featured_image?.src
      || group.featured_image?.src
      || group.images?.[0]?.src;
    const rawHoverSrc = group.images?.[1]?.src || null;

    if (rawSrc) {
      const sizedSrc = sizeShopifyImage(rawSrc, '400x400');
      productBlock.querySelectorAll('.rebuy-product-image img:not(.rebuy-product-img-hover), .rebuy-product-image-trigger img:not(.rebuy-product-img-hover)').forEach((img) => {
        img.src = sizedSrc;
      });
      // Also update background-image if Rebuy's imageStyles uses it on the container
      productBlock.querySelectorAll('.rebuy-product-image, .rebuy-product-image-trigger').forEach((el) => {
        const bg = window.getComputedStyle(el).backgroundImage;
        if (bg && bg !== 'none' && bg !== '') {
          el.style.backgroundImage = `url(${sizedSrc})`;
        }
      });
    }

    // Update hover image to the swapped product's second image
    productBlock.querySelectorAll('.rebuy-product-img-hover').forEach((img) => {
      if (rawHoverSrc) {
        img.src = sizeShopifyImage(rawHoverSrc, '400x400');
        img.style.display = '';
      } else {
        img.style.display = 'none';
      }
    });

    storedWidget.View.vue.$forceUpdate();
  }, true);

  document.addEventListener('rebuy.productsChange', async (event) => {
    const widget = event.detail?.widget;
    if (widget?.id !== YMAL_WIDGET_ID) return;
    if (!widget?.data?.products?.length) return;

    storedWidget = widget;
    await Promise.all(widget.data.products.map((p) => stitchProduct(p)));
    widget.View.vue.$forceUpdate();
  });
})();
